<script lang="ts">
  import { supabase } from '../lib/supabase';
  import { navigationActions } from '../stores/navigation';
  import { userLanguagesActions, activeLanguage } from '../stores/userLanguages';
  import { userSettings } from '../stores/settings';
  import {
    ChevronLeft,
    Globe,
    FileText,
    Loader,
    AlertCircle,
    CheckCircle,
    Clipboard,
    Image as ImageIcon,
    Sparkles,
  } from 'lucide-svelte';
  import Button from './ui/button.svelte';
  import Card from './ui/card.svelte';
  import { _ } from 'svelte-i18n';
  import { resolveApiUrl } from '../lib/utils';
  import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
  import type { ProficiencyLevel } from '../stores/settings';

  type ImportMode = 'url' | 'clipboard' | 'image';

  let importMode = $state<ImportMode>('url');
  let url = $state('');
  let title = $state('');
  let content = $state('');
  let summary = $state('');
  let selectedLanguage = $state('');
  let tags = $state('');
  let isLoading = $state(false);
  let isFetching = $state(false);
  let isSummarizing = $state(false);
  let error = $state('');
  let warning = $state('');
  let detectedLanguage = $state('');
  let isProcessingImage = $state(false);

  const targetLang = $derived($activeLanguage?.language?.code || 'en');
  const proficiencyLevel = $derived(($userSettings?.proficiency_level || 'A1') as ProficiencyLevel);

  async function fetchFromURL() {
    if (!url.trim()) {
      error = $_('texts.add.url_required');
      return;
    }

    isFetching = true;
    error = '';
    warning = '';

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        error = $_('common.must_be_logged_in');
        return;
      }

      const response = await fetch(resolveApiUrl('/api/articles/fetch-url'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          url: url.trim(),
          userId: user.id,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        error = data.error || $_('texts.add.fetch_error');
        if (data.details) {
          error += `: ${data.details}`;
        }
        return;
      }

      title = data.title || $_('texts.add.title_untitled');
      content = data.content || '';

      // Handle truncated texts
      if (data.truncated) {
        const originalLength = data.originalLength;
        const characterCount = data.characterCount;
        warning = $_('texts.add.content_truncated', {
          values: { originalLength, characterCount }
        });
      }

      // Detect language
      await detectLanguage(content);

      // Generate summary for the text
      if (content && selectedLanguage) {
        await generateSummary(content, selectedLanguage);
      }

    } catch (err) {
      console.error('Fetch error:', err);
      error = $_('texts.add.load_error');
    } finally {
      isFetching = false;
    }
  }

  async function detectLanguage(text: string) {
    if (!text || text.length < 10) return;

    try {
      const response = await fetch(resolveApiUrl('/api/articles/detect-language'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text: text.substring(0, 1000) }),
      });

      const data = await response.json();

      if (response.ok && data.detected) {
        detectedLanguage = data.language;
        selectedLanguage = data.language;

        // Warning if detected language != target language
        if (data.language !== targetLang) {
          const detectedLang = getLanguageName(data.language);
          const targetLangName = getLanguageName(targetLang);
          warning = $_('texts.add.language_mismatch', {
            values: { detectedLang, targetLang: targetLangName }
          });
        }
      }
    } catch (err) {
      console.error('Language detection error:', err);
    }
  }

  async function generateSummary(text: string, language: string) {
    if (!text || text.length < 100) {
      // Too short for summarization
      summary = text;
      return;
    }

    isSummarizing = true;

    try {
      const response = await fetch(resolveApiUrl('/api/summarize'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text,
          language,
          proficiencyLevel,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        summary = data.summary;
        console.log(`Summary generated: ${data.metadata.originalWordCount} → ${data.metadata.summaryWordCount} words`);
      } else {
        console.error('Summary generation failed:', data.error);
        // Fall back to original content if summary fails
        summary = '';
      }
    } catch (err) {
      console.error('Summary generation error:', err);
      summary = '';
    } finally {
      isSummarizing = false;
    }
  }

  async function saveText() {
    if (!title.trim() || !content.trim()) {
      error = $_('texts.add.title_content_required');
      return;
    }

    if (!selectedLanguage) {
      error = $_('texts.add.select_language');
      return;
    }

    const wordCount = content.trim().split(/\s+/).length;

    isLoading = true;
    error = '';

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        error = $_('common.must_be_logged_in');
        return;
      }

      // Create text
      const { data: text, error: createError } = await (supabase
        .from('texts' as any)
        .insert({
          user_id: user.id,
          title: title.trim(),
          content: content.trim(),
          summary: summary.trim() || null,
          language: selectedLanguage,
          url: importMode === 'url' ? url.trim() : null,
          word_count: wordCount,
        })
        .select()
        .single() as any);

      if (createError) {
        error = $_('texts.add.save_error');
        console.error('Create error:', createError);
        return;
      }

      // Add tags if provided
      if (tags.trim() && text) {
        const tagArray = tags
          .split(',')
          .map(t => t.trim())
          .filter(t => t.length > 0);

        if (tagArray.length > 0) {
          const tagInserts = tagArray.map(tag => ({
            text_id: text.id,
            tag: tag.substring(0, 50),
          }));

          await (supabase.from('text_tags' as any).insert(tagInserts) as any);
        }
      }

      // Auto-create flashcard category for this text
      try {
        // Get target language ID from the text's language code
        const { data: targetLangs } = await supabase
          .from('flashcard_target_languages')
          .select('id')
          .eq('code', selectedLanguage)
          .maybeSingle();

        if (targetLangs) {
          // Check if category with this name already exists for this user and language
          const { data: existingCategory } = await supabase
            .from('flashcard_categories')
            .select('id')
            .eq('user_id', user.id)
            .eq('name', title.trim())
            .eq('target_language_id', targetLangs.id)
            .maybeSingle();

          // Create category only if it doesn't exist
          if (!existingCategory) {
            const { error: categoryError } = await supabase
              .from('flashcard_categories')
              .insert({
                user_id: user.id,
                name: title.trim().substring(0, 255),
                description: importMode === 'url' ? url.trim() : null,
                target_language_id: targetLangs.id,
              });

            if (categoryError) {
              console.warn('Could not create category for text:', categoryError);
            }
          }
        }
      } catch (categoryErr) {
        // Don't fail text creation if category creation fails
        console.warn('Could not create category for text:', categoryErr);
      }

      // Navigate to text
      navigationActions.viewText(String(text!.id));

    } catch (err) {
      console.error('Save error:', err);
      error = $_('texts.add.save_error_msg');
    } finally {
      isLoading = false;
    }
  }

  function getLanguageName(code: string): string {
    const languages: Record<string, string> = {
      'en': 'Angličtina',
      'de': 'Němčina',
      'fr': 'Francouzština',
      'es': 'Španělština',
      'it': 'Italština',
      'cs': 'Čeština',
      'ar': 'Arabština',
      'ja': 'Japonština',
      'zh': 'Čínština',
    };
    return languages[code] || code.toUpperCase();
  }

  async function captureImage(source: CameraSource) {
    try {
      isProcessingImage = true;
      error = '';

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        error = $_('common.must_be_logged_in');
        return;
      }

      const image = await Camera.getPhoto({
        quality: 60,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: source,
        width: 1200,
      });

      if (!image.base64String) {
        throw new Error('Failed to capture image');
      }

      const imageData = `data:image/${image.format};base64,${image.base64String}`;

      // Process image with OCR
      const response = await fetch(resolveApiUrl('/api/extract-text-from-image'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ image: imageData }),
      });

      const data = await response.json();

      if (!response.ok) {
        error = data.error || 'Failed to process image';
        return;
      }

      // Set extracted text to content
      content = data.text || '';

      // Detect language and generate summary
      if (content) {
        await detectLanguage(content);
        if (selectedLanguage) {
          await generateSummary(content, selectedLanguage);
        }
      }

    } catch (err) {
      console.error('Error processing image:', err);
      error = err instanceof Error ? err.message : 'Failed to process image';
    } finally {
      isProcessingImage = false;
    }
  }

  async function takePhoto() {
    await captureImage(CameraSource.Camera);
  }

  async function pickFromGallery() {
    await captureImage(CameraSource.Photos);
  }

  $effect(() => {
    if (importMode === 'clipboard' && content && !detectedLanguage) {
      detectLanguage(content).then(() => {
        if (selectedLanguage && content) {
          generateSummary(content, selectedLanguage);
        }
      });
    }
  });
</script>

<div class="min-h-screen pb-24 bg-gradient-subtle">
    <div class="mx-auto max-w-2xl px-4 py-6 space-y-6">
      <!-- Header -->
      <div class="flex items-center gap-3">
        <Button onclick={() => navigationActions.goToTexts()} variant="ghost" class="btn btn-back" aria-label={$_('navigation.back')}>
          <ChevronLeft class="w-5 h-5" />
        </Button>
      </div>
      
      <h2 class="text-2xl font-bold text-center mb-6">
        {$_('texts.add.title')}
      </h2>

      <!-- Import Mode Selector with 3 tabs -->
      <div class="tab-switcher grid grid-cols-3">
        <button
          onclick={() => { importMode = 'url'; error = ''; warning = ''; }}
          class="tab-item {importMode === 'url' ? 'active' : ''}"
        >
          <Globe class="h-4 w-4" />
          <span class="text-xs">{$_('texts.add.from_url')}</span>
        </button>
        <button
          onclick={() => { importMode = 'clipboard'; error = ''; warning = ''; }}
          class="tab-item {importMode === 'clipboard' ? 'active' : ''}"
        >
          <Clipboard class="h-4 w-4" />
          <span class="text-xs">{$_('texts.add.from_clipboard')}</span>
        </button>
        <button
          onclick={() => { importMode = 'image'; error = ''; warning = ''; }}
          class="tab-item {importMode === 'image' ? 'active' : ''}"
        >
          <ImageIcon class="h-4 w-4" />
          <span class="text-xs">{$_('texts.add.from_image')}</span>
        </button>
      </div>

      {#if importMode === 'url'}
        <!-- URL Import -->
        <Card class="p-6 space-y-4">
          <div>
            <label for="url-input" class="form-label">
              {$_('texts.add.url_label')}
            </label>
            <div class="flex gap-2">
              <input
                id="url-input"
                type="url"
                bind:value={url}
                placeholder="https://example.com/article"
                class="form-input flex-1"
              />
              <Button
                onclick={fetchFromURL}
                disabled={isFetching}
                class="btn btn-action"
              >
                {#if isFetching}
                  <Loader class="h-5 w-5 animate-spin" />
                {:else}
                  {$_('texts.add.fetch_button')}
                {/if}
              </Button>
            </div>
          </div>
        </Card>
      {:else if importMode === 'image'}
        <!-- Image Import -->
        <Card class="p-6 space-y-4">
          <div class="grid grid-cols-2 gap-4">
            <Button
              onclick={takePhoto}
              disabled={isProcessingImage}
              class="btn btn-action h-24 flex flex-col gap-2"
            >
              {#if isProcessingImage}
                <Loader class="h-8 w-8 animate-spin" />
              {:else}
                <ImageIcon class="h-8 w-8" />
                <span>{$_('photo_capture.take_photo')}</span>
              {/if}
            </Button>
            <Button
              onclick={pickFromGallery}
              disabled={isProcessingImage}
              class="btn btn-action h-24 flex flex-col gap-2"
            >
              {#if isProcessingImage}
                <Loader class="h-8 w-8 animate-spin" />
              {:else}
                <ImageIcon class="h-8 w-8" />
                <span>{$_('photo_capture.from_gallery')}</span>
              {/if}
            </Button>
          </div>
        </Card>
      {/if}

      <!-- Error/Warning Messages -->
      {#if error}
        <Card class="p-4 border border-destructive/30 bg-destructive/10">
          <div class="flex items-center gap-2 text-destructive">
            <AlertCircle class="h-5 w-5 flex-shrink-0" />
            <p class="text-sm">{error}</p>
          </div>
        </Card>
      {/if}

      {#if warning}
        <Card class="p-4 border border-warning/30 bg-warning/10">
          <div class="flex items-center gap-2 text-warning">
            <AlertCircle class="h-5 w-5 flex-shrink-0" />
            <p class="text-sm">{warning}</p>
          </div>
        </Card>
      {/if}

      <!-- Text Form -->
      {#if importMode === 'clipboard' || (importMode === 'url' && content) || (importMode === 'image' && content)}
        <Card class="p-6 space-y-4">
          <!-- Title -->
          <div>
            <label for="text-title" class="form-label">
              {$_('texts.add.title_label')}
            </label>
            <input
              id="text-title"
              type="text"
              bind:value={title}
              placeholder="{$_('texts.add.title_placeholder')}"
              class="form-input"
            />
          </div>

          <!-- Content -->
          <div>
            <label for="text-content" class="form-label">
              {$_('texts.add.content_label')}
            </label>
            <textarea
              id="text-content"
              bind:value={content}
              placeholder="{$_('texts.add.content_placeholder')}"
              rows="12"
              class="form-textarea"
            ></textarea>
            <p class="mt-2 text-xs">
              {content.trim().split(/\s+/).length} {$_('texts.add.word_label_plural')} • {content.length} {$_('texts.add.char_label_plural')} ({$_('texts.add.max_content')})
            </p>
          </div>

          <!-- Summary Info -->
          {#if isSummarizing}
            <Card class="p-4 border border-primary/30 bg-primary/10">
              <div class="flex items-center gap-2 text-primary">
                <Loader class="h-5 w-5 animate-spin flex-shrink-0" />
                <p class="text-sm">{$_('texts.add.generating_summary')}</p>
              </div>
            </Card>
          {:else if summary}
            <Card class="p-4 border border-primary/30 bg-primary/10">
              <div class="flex items-start gap-2">
                <Sparkles class="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p class="text-sm font-medium text-primary">{$_('texts.add.summary_ready')}</p>
                  <p class="text-xs text-muted-foreground mt-1">
                    {$_('texts.add.summary_info', { values: { level: proficiencyLevel } })}
                  </p>
                  <p class="text-sm mt-2 text-foreground/80 line-clamp-3">{summary}</p>
                </div>
              </div>
            </Card>
          {:else if content.length > 100}
            <Card class="p-4 border border-muted/30 bg-muted/10">
              <div class="flex items-center gap-2 text-muted-foreground">
                <Sparkles class="h-5 w-5 flex-shrink-0" />
                <p class="text-sm">{$_('texts.add.summary_will_generate')}</p>
              </div>
            </Card>
          {/if}

          <!-- Language -->
          <div>
            <label for="text-language" class="form-label">
              {$_('texts.add.language_label')}
            </label>
            <select
              id="text-language"
              bind:value={selectedLanguage}
              class="form-select"
            >
              <option value="">{$_('language.select_language')}</option>
              <option value="en">{$_('language.names.en')}</option>
              <option value="de">{$_('language.names.de')}</option>
              <option value="fr">{$_('language.names.fr')}</option>
              <option value="es">{$_('language.names.es')}</option>
              <option value="it">{$_('language.names.it')}</option>
              <option value="ar">{$_('language.names.ar')}</option>
              <option value="ja">{$_('language.names.ja')}</option>
              <option value="zh">{$_('language.names.zh')}</option>
            </select>
            {#if detectedLanguage}
              <p class="mt-2 text-xs text-primary flex items-center gap-1">
                <CheckCircle class="h-4 w-4" />
                {$_('texts.add.detected_language')}: {getLanguageName(detectedLanguage)}
              </p>
            {/if}
          </div>

          <!-- Tags -->
          <div>
            <label for="tags-input" class="form-label">
              {$_('texts.add.tags_label')}
            </label>
            <input
              id="tags-input"
              type="text"
              bind:value={tags}
              placeholder="{$_('texts.add.tags_placeholder')}"
              class="form-input"
            />
          </div>

          <!-- Actions -->
          <div class="flex gap-3 pt-4">
            <Button
              onclick={() => navigationActions.goToTexts()}
              variant="outline"
              class="btn btn-action flex-1"
            >
              {$_('buttons.cancel')}
            </Button>
            <Button
              onclick={saveText}
              disabled={isLoading}
              class="btn btn-action flex-1"
            >
              {#if isLoading}
                <Loader class="h-5 w-5 animate-spin mx-auto" />
              {:else}
                {$_('texts.add.save_text')}
              {/if}
            </Button>
          </div>
        </Card>
      {/if}
    </div>
  </div>
